import type { RFPContext, SectionId } from "./types";
import { sanitizeBuyerName } from "./contextSanitize";

/**
 * Admin / commercial boilerplate library (spec "clause library").
 *
 * Canonical RFP administration & procurement clauses, versioned. The pack
 * generator never invents this text: it is inserted verbatim by
 * `applyAdminClauses`, with only names / dates / currency / [TBD] filled from
 * the context object. Mirror of legalStubs.ts — same invariants.
 *
 * Clause groups map onto EXISTING sections only (0, 3, 4, 6, 7, 8, 11, 12, 13).
 * No new sections, no new generator step.
 */

export interface AdminClause {
  key: string;
  version: string;
  section: SectionId;
  title: string;
  body: string;
}

const TBD = "[TBD]";

function buyer(ctx: RFPContext): string {
  // A "Who …" / "What …" phrase from a draft or backend must never bind as the
  // buyer entity. Non-entity words are rejected by the sanitizer; the
  // genuinely-derived name or [TBD] is returned.
  return sanitizeBuyerName(ctx.meta.buyer_entity) || sanitizeBuyerName(ctx.meta.client_name) || TBD;
}

function maybe(value: string | null | undefined): string {
  return value && value.trim() ? value : TBD;
}

function languageLabel(code: string | null | undefined): string {
  const map: Record<string, string> = {
    "en-GB": "English",
    "en-US": "English",
    fr: "French",
    de: "German",
    es: "Spanish",
    nl: "Dutch",
    it: "Italian",
    pl: "Polish",
    pt: "Portuguese",
    "nl-BE": "Dutch (Belgium)",
    "fr-CA": "French (Canada)",
    "en-CA": "English (Canada)",
  };
  if (!code || !code.trim()) return TBD;
  return map[code.trim().toLowerCase()] ?? code;
}

// ─── Section 0 — cover & confidentiality banner ──────────────────────────────

function confidentialityBanner(ctx: RFPContext): AdminClause {
  return {
    key: "confidentiality_banner",
    version: "admin.v1",
    section: "s00_cover",
    title: "Confidentiality",
    body:
      `> **Notice.** This document is provided solely for the purpose of proposal preparation. ` +
      `The information herein is confidential and proprietary to ${buyer(ctx)} and is not to be ` +
      `disclosed to any third party without the prior written consent of ${buyer(ctx)}.`,
  };
}

// ─── Section 3 — proposal response instructions ──────────────────────────────

function singleContact(ctx: RFPContext): AdminClause {
  const lead = ctx.stakeholders.procurement_lead?.trim() ?? "";
  const buyerName = buyer(ctx);
  const contact =
    lead && buyerName && lead.toLowerCase() !== buyerName.toLowerCase()
      ? `**${lead}** at ${buyerName}`
      : lead || buyerName;
  const email = ctx.stakeholders.contact_email?.trim() ?? "";
  const contactLine = email ? `- Contact: ${contact} — ${email}.` : `- Contact: ${contact}.`;
  return {
    key: "single_contact",
    version: "admin.v1",
    section: "s03_response",
    title: "Communication — single point of contact",
    body: [
      `- All proposals, requests for additional information, questions (technical or contractual), contract responses and other correspondence are processed through the single contact below and are to be submitted in writing or by e-mail only.`,
      contactLine,
      `- Where information given to one prospective service provider could advantage that provider, it is promptly furnished to all other prospective service providers, unless the response is obviously subject to commercial confidentiality.`,
      `- All documentation and correspondence is in **${languageLabel(ctx.meta.language)}**.`,
    ].join("\n"),
  };
}

function dueDates(ctx: RFPContext): AdminClause {
  const hardCopy = ctx.process.hard_copy
    ? `- A hard copy is accepted as an option in addition to the electronic copy, delivered to the contact above.`
    : `- Electronic submission only; no hard copies are required.`;
  return {
    key: "due_dates",
    version: "admin.v1",
    section: "s03_response",
    title: "Due dates",
    body: [
      `- Proposals are due no later than **${maybe(ctx.process.deadline)}**${ctx.process.timezone ? ` (${ctx.process.timezone})` : ""}.`,
      `- Deliver one electronic copy of the proposal, containing all requested information, via ${maybe(ctx.process.issue_method)} to the contact above.`,
      hardCopy,
      `- Late submissions are ${ctx.process.late_policy === "reject" ? "rejected and may be de-selected from the process" : "accepted only at the buyer's discretion"}.`,
    ].join("\n"),
  };
}

function scheduleOfEvents(ctx: RFPContext): AdminClause {
  return {
    key: "schedule_of_events",
    version: "admin.v1",
    section: "s03_response",
    title: "Schedule of events",
    body: [
      `This RFP represents phase 1 of a procurement process planned to result in the letting of an agreement. The following is the anticipated procurement plan and timetable of events:`,
      `- Issue RFP to service providers — ${maybe(ctx.process.issue_date)}`,
      `- Service provider submittal of questions — ${maybe(ctx.process.qa_deadline)}`,
      `- Response to all questions — ${maybe(ctx.process.qa_response_date)}`,
      `- Proposal due date — ${maybe(ctx.process.deadline)}`,
      `- Presentations by service providers — ${maybe(ctx.process.presentation_date)}`,
      `- Anticipated final decision and selection — ${maybe(ctx.process.award_target_date)}`,
      ``,
      `${buyer(ctx)} reserves the right to amend this timetable should events dictate.`,
    ].join("\n"),
  };
}

function submissionRules(): AdminClause {
  return {
    key: "submission_rules",
    version: "admin.v1",
    section: "s03_response",
    title: "Response format & de-selection",
    body: [
      `- Respond in the prescribed format with all the information requested. Failure to provide the information in the prescribed format, or not replying within the stated time period, may lead to your company being de-selected from the process.`,
      `- Proposals are submitted in the sections set out in these instructions; the required format and content of each section must be followed.`,
      `- Confine the submission to essential matters sufficient to define the proposal and provide an adequate basis for evaluation. Supporting documents must not be a blanket reference to general brochures or web information.`,
    ].join("\n"),
  };
}

// ─── Section 4 — qualifications / company information ────────────────────────

function vendorQuestionnaire(): AdminClause {
  return {
    key: "vendor_questionnaire",
    version: "admin.v1",
    section: "s04_qualifications",
    title: "Vendor questionnaire",
    body: [
      `- Complete and return the Vendor Questionnaire (Appendix L, Vendor_Questionnaire.xlsx) with the proposal.`,
      `- Its purpose is to establish that the organisation is structurally, financially and legally sound, and has the experience and capacities to fulfil the requirements of this RFP.`,
    ].join("\n"),
  };
}

/**
 * Structured "Ask:" / "Question N:" clauses extracted from the buyer's source
 * document, re-routed from the internal strategy notes into Appendix L (Vendor
 * Questionnaire). Rendered under Section 4 only; returns null when the source
 * held no such internal notes.
 */
function vendorQuestionnaireExtras(ctx: RFPContext): AdminClause | null {
  const questions = (ctx.vendor_questions ?? []).filter(Boolean);
  if (!questions.length) return null;
  return {
    key: "vendor_questionnaire_extras",
    version: "admin.v1",
    section: "s04_qualifications",
    title: "Vendor questionnaire — additional questions",
    body: [
      `- Respond to the following questions raised in the buyer's briefing. They are included in Appendix L (Vendor_Questionnaire.xlsx) and must be completed with each proposal:`,
      ...questions.map((q, i) => `  ${i + 1}. ${q}`),
    ].join("\n"),
  };
}

// ─── Section 6 — solution narrative extras ───────────────────────────────────

function execSummary(): AdminClause {
  return {
    key: "exec_summary",
    version: "admin.v1",
    section: "s06_solution",
    title: "Introduction & executive summary",
    body: [
      `- The introduction must include the names and contact details of the authors, with the appropriate contacts for clarification points and meeting invitations.`,
      `- Provide a high-level executive summary of the proposal: a short synopsis that outlines the main features and benefits, showing specifically how the proposal meets the buyer's objectives, and describing the key factors that differentiate the offer from those in the marketplace. It is suitable for distribution to senior management.`,
    ].join("\n"),
  };
}

function secondaryProposal(): AdminClause {
  return {
    key: "secondary_proposal",
    version: "admin.v1",
    section: "s06_solution",
    title: "Alternative solutions & partial compliance",
    body: [
      `- The service provider has the option to provide other solutions that may be available, submitted as a secondary proposal.`,
      `- If a solution cannot meet, or can only partly meet, a particular requirement, this must be clearly stated in the compliance matrix and in the proposal narrative.`,
    ].join("\n"),
  };
}

// ─── Section 7 — commercial / pricing rules ──────────────────────────────────

function pricingRules(ctx: RFPContext): AdminClause {
  const taxClause = ctx.commercial.tax_exclusive
    ? `- All prices are to be stated exclusive of applicable taxes (e.g. VAT or similar).`
    : `- All prices are to be stated inclusive of applicable taxes.`;
  const fixClause =
    ctx.commercial.pricing_basis === "cost_plus"
      ? `- The offer is submitted on a cost-plus basis; the percentage mark-up will be the agreed mark-up for the duration of the agreement.`
      : `- The offer is submitted on the basis that the rates and reimbursable cost elements included are fixed for the first **${ctx.commercial.price_fix_months || TBD} months** of the agreement. Thereafter price changes are managed in accordance with the relevant clauses within the Master Services Agreement.`;
  const volumeClause = ctx.commercial.volume_guaranteed
    ? `- The buyer confirms a minimum volume of business across the term, as set out in the scope.`
    : `- Indicative frequency and size of demand are given strictly as an indication only of potential volume of business; no guarantee is provided within this RFP as to the volume of business to be received.`;
  return {
    key: "pricing_rules",
    version: "admin.v1",
    section: "s07_commercial",
    title: "General pricing rules",
    body: [
      `- All elements of the price proposal must be detailed in the response; the buyer will pay no extra charges unless included therein.`,
      `- All prices are quoted in **${maybe(ctx.meta.currency)}**.`,
      taxClause,
      fixClause,
      volumeClause,
    ].join("\n"),
  };
}

// ─── Section 8 — service levels questionnaire ────────────────────────────────

function slaQuestionnaire(): AdminClause {
  return {
    key: "sla_questionnaire",
    version: "admin.v1",
    section: "s08_sla",
    title: "Service level questionnaire",
    body: [
      `- As part of the agreement a Service Level Agreement (SLA) will be agreed between the parties.`,
      `- Respond to the questions in the Service Level Definition questionnaire (Appendix M, SLA_Questionnaire.xlsx); the answers will assist in the compilation of this SLA.`,
      `- Key features of the SLA will include, but not be limited to: implementation / support team; customer services / support; ordering procedures; customer billing / invoicing; customer service levels; escalation process and problem resolution.`,
    ].join("\n"),
  };
}

// ─── Section 11 — contract & legal ───────────────────────────────────────────

function msaRedlineTiers(ctx: RFPContext): AdminClause | null {
  if (!ctx.legal_flags.msa_redline_tiers) return null;
  return {
    key: "msa_redline_tiers",
    version: "admin.v1",
    section: "s11_contract",
    title: "MSA redline tiers",
    body: [
      `- Submittals that do not propose material changes to the standard Master Services Agreement will be classified as **Tier 1** and will receive first consideration. All other proposals will be classified as **Tier 2**.`,
      `- At the proposal stage, state any variation requested to the terms and conditions outlined in the MSA, including the reason(s) for the variation and proposed alternative wording.`,
      `- For provisions that are readily acceptable, respond **"Read and Understood"**. All other responses are accompanied by an explanation and, where appropriate, proposed changes, made using Microsoft Track Changes on the standard MSA (Appendix) and returned with the proposal submission.`,
    ].join("\n"),
  };
}

function generalConditions(ctx: RFPContext): AdminClause {
  const buyerName = buyer(ctx);
  return {
    key: "general_conditions",
    version: "admin.v1",
    section: "s11_contract",
    title: "RFP general conditions",
    body: [
      `**Confidentiality & restrictions on disclosure.** All information provided must be held confidential and not disclosed to any third party by any bidder or service provider that receives this RFP. All materials contained in this RFP, or later distributed or referred to, including descriptions of the buyer and its products, systems, procedures, methodologies and employee information, are and shall remain the property of ${buyerName}. By accepting this RFP the service provider agrees to keep such materials and information in confidence, not to provide duplicates or disclose them to any person outside its organisation, and not to copy or reproduce them in any way or form without the express written permission of ${buyerName}. ${buyerName} is free to make use of any information passed on, retains the right to reproduce the responses for its own internal use, and the responses (including associated materials) will not be returned. If a proposal includes information to be kept from any other party, or used only for evaluation, those pages must be marked accordingly.`,
      ``,
      `**Accuracy of information.** While reasonable care has been taken in the preparation of this RFP and the information appended hereto, ${buyerName} does not warrant the contents and will not be liable for any liabilities incurred as a result of reliance thereon. ${buyerName} will only accept changes, prior to signature, to the bid price insofar as the change reflects an inaccurate or misleading statement in the RFP.`,
      ``,
      `**Proposal validity.** Proposals remain valid for a minimum of **${ctx.commercial.price_validity_days ? `${ctx.commercial.price_validity_days} days` : TBD}** from the proposal due date stated in Section 3.`,
      ``,
      `**RFP changes.** After the issuance of this RFP and before the date set for receipt of proposals, ${buyerName} reserves the right to make changes to this RFP, including but not limited to its specifications, to correct defects or ambiguities, or to change the closing date. All candidates will be notified of any new day and time for proposal submission.`,
      ``,
      `**Discontinuation.** While every intention exists to proceed with the award of an agreement for this project, ${buyerName} at its discretion and without explanation may at any time choose to discontinue this RFP without obligation.`,
      ``,
      `**Costs of the RFP.** Any expenditure, work or effort undertaken prior to an offer of contract and acceptance thereof, including any requests for further clarification or information whether in person or in writing, is a matter solely for the bidding company.`,
    ].join("\n"),
  };
}

// ─── Section 12 — evaluation ─────────────────────────────────────────────────

function reservationOfRights(ctx: RFPContext): AdminClause {
  const awardModel =
    ctx.evaluation.award_model === "exclusive"
      ? `- Award a single, exclusive agreement, evaluated on the total program and total cost quoted for all elements of the service.`
      : ctx.evaluation.award_model === "split_ok"
        ? `- Award the agreement in full, or for any combination of sites and service components, depending on the offers received.`
        : ctx.evaluation.award_model === "multi_ok"
          ? `- Award more than one contract for the services in scope.`
          : null;
  const modelLine = awardModel ? `\nThe following award approach applies:\n${awardModel}` : "";
  return {
    key: "reservation_of_rights",
    version: "admin.v1",
    section: "s12_evaluation",
    title: "Reservation of rights",
    body: [
      `At its sole discretion, ${buyer(ctx)} reserves the right to:`,
      `- Reject any or all offers and discontinue this RFP process without obligation.`,
      `- Accept other than the lowest offer.`,
      `- Award a contract on the basis of initial offers received, without discussions or requests for best and final offers.`,
      `- Award more than one contract.`,
      modelLine,
      ``,
      `This RFP may not result in an offer of contract. ${buyer(ctx)} is under no obligation in respect of any response provided to this RFP.`,
    ]
      .filter((l) => l !== null)
      .join("\n"),
  };
}

// ─── Section 13 — appendix index additions ───────────────────────────────────

function appendixIndex(): AdminClause {
  return {
    key: "appendix_index",
    version: "admin.v1",
    section: "s13_appendices",
    title: "Appendices — additional",
    body: [
      `- **Appendix L:** Vendor questionnaire (Vendor_Questionnaire.xlsx)`,
      `- **Appendix M:** Service Level Definition questionnaire (SLA_Questionnaire.xlsx)`,
      `- **Attachment A:** Compliance matrix (Appendix C) and pricing template (Appendix J) are issued separately and are never merged.`,
    ].join("\n"),
  };
}

// ─── Public API ─────────────────────────────────────────────────────────────

/**
 * The applicable admin clause set for a context. Clauses are versioned and
 * slot-filled; missing context renders as [TBD] (and is surfaced in the gaps
 * register by buildGaps).
 */
export function getAdminClauses(ctx: RFPContext): AdminClause[] {
  const clauses: AdminClause[] = [
    confidentialityBanner(ctx),
    singleContact(ctx),
    dueDates(ctx),
    scheduleOfEvents(ctx),
    submissionRules(),
    vendorQuestionnaire(),
    execSummary(),
    secondaryProposal(),
    pricingRules(ctx),
    slaQuestionnaire(),
    generalConditions(ctx),
    reservationOfRights(ctx),
    appendixIndex(),
  ];

  const msa = msaRedlineTiers(ctx);
  if (msa) clauses.push(msa);

  const vqExtras = vendorQuestionnaireExtras(ctx);
  if (vqExtras) clauses.push(vqExtras);

  return clauses;
}

/** Footer text for the docx export — mirrors the cover confidentiality banner. */
export function confidentialityFooter(ctx: RFPContext): string {
  const b = buyer(ctx);
  return `Proposal preparation only — confidential and proprietary to ${b}; not for disclosure to any third party without the prior written consent of ${b}.`;
}